import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type CSSProperties } from 'react';
import {
  dateComplete,
  type CouleurAvatar,
  type HolidaySourceKind,
  type IcsHolidayPeriod,
  type Locale,
  type Messages,
  type SchoolZone,
} from '@repere/core';
import { messageDErreur, useSession } from '../auth/session';
import { useTraduction } from '../i18n/langue';
import { nomDuDossier } from '../dossier/prenoms';
import { Alerte, Bouton, Carte } from '../ui';
import type { DossierEnCarte } from '../accueil/CarteDossier';

/** Une source en vigueur, qu'elle couvre le dossier entier ou un seul enfant. */
export interface SourceEnVigueur {
  kind: HolidaySourceKind;
  /** La zone déduite du code postal de l'école — rendue quelle que soit la source. */
  zone: SchoolZone | null;
  periods: IcsHolidayPeriod[] | null;
  file: {
    name: string;
    importedAt: string;
    periodCount: number;
    coverage: { from: string; to: string } | null;
  } | null;
}

export interface SourceEnfant extends SourceEnVigueur {
  childId: string;
  firstName: string;
  /** Sa couleur d'avatar, celle sous laquelle il apparaît partout ailleurs. */
  color: CouleurAvatar;
}

/** Ce que rend `GET /case-files/:id/holiday-source`. */
export interface SourceCongesView {
  /** « Les enfants ont les mêmes congés » — vrai par défaut, et c'est le cas courant. */
  shared: boolean;
  dossier: SourceEnVigueur;
  children: SourceEnfant[];
}

/** La clé de cache, partagée avec le calendrier : régler ici met à jour l'écran là-bas. */
export const cleSourceConges = (caseFileId: string): string[] => ['source-conges', caseFileId];

/**
 * La teinte d'un enfant, telle que la feuille de style la connaît.
 *
 * `--conge-<couleur>` et non `--avatar-<couleur>-trait` : l'anneau d'un jour de vacances se
 * pose sur la couleur de garde de la case, qui suit le thème. La feuille de style choisit,
 * parmi les deux valeurs de sa couleur, celle qui se voit — le trait en clair, le fond pâle
 * en sombre, où un trait à #002395 disparaîtrait dans un bleu profond.
 */
export const teinteEnfant = (couleur: CouleurAvatar): string => `var(--conge-${couleur})`;

/**
 * Une date ISO nue, lue à midi.
 *
 * `new Date('2026-10-17')` désigne minuit **UTC** : à l'ouest de Greenwich, la date
 * affichée reculerait d'un jour. Midi met la journée à l'abri de tous les fuseaux.
 */
const jour = (iso: string, locale: Locale): string =>
  dateComplete(new Date(`${iso}T12:00:00`), locale);

/**
 * D'où un dossier tient ses congés scolaires.
 *
 * **Le réglage se pose ici, mais il appartient au dossier.** Deux parents lisent le même
 * calendrier de garde ; une source par personne les ferait diverger sur la question à
 * laquelle ce calendrier existe pour répondre — chez qui dort l'enfant. Chaque bloc le dit
 * en toutes lettres, sans quoi on croirait ne régler que son propre affichage.
 *
 * **Une fratrie n'a pas forcément les mêmes congés.** Un collège et un lycée de part et
 * d'autre d'une limite d'académie suffisent à la séparer. L'option ne s'affiche qu'à partir
 * de deux enfants : la poser sur un dossier qui n'en a qu'un serait une question sans objet.
 *
 * La liste des sources est faite pour s'allonger : elle vient du noyau, et cet écran ne
 * connaît que ce que le serveur lui rend.
 */
export function SourceConges() {
  const { client } = useSession();
  const { messages } = useTraduction();
  const m = messages.compte;

  const dossiers = useQuery({
    queryKey: ['case-files'],
    queryFn: () => client.request<DossierEnCarte[]>('/case-files'),
  });

  return (
    <Carte titre={m.conges}>
      <p style={{ marginTop: 0 }}>{m.congesAide}</p>

      {dossiers.data && dossiers.data.length === 0 ? (
        <p className="discret" style={{ marginBottom: 0 }}>
          {m.congesAucunDossier}
        </p>
      ) : null}

      {(dossiers.data ?? []).map((dossier) => (
        <SourceDuDossier key={dossier.id} dossier={dossier} />
      ))}
    </Carte>
  );
}

function SourceDuDossier({ dossier }: { dossier: DossierEnCarte }) {
  const { client } = useSession();
  const { locale, messages } = useTraduction();
  const m = messages.compte;
  const cache = useQueryClient();
  /** L'erreur de dépôt, et sur quel bloc l'afficher — `null` pour celui du dossier. */
  const [erreurFichier, setErreurFichier] = useState<{
    childId: string | null;
    message: string;
  } | null>(null);

  const cle = cleSourceConges(dossier.id);
  const source = useQuery({
    queryKey: cle,
    queryFn: () => client.request<SourceCongesView>(`/case-files/${dossier.id}/holiday-source`),
  });

  /** Toute écriture rend l'état à jour : on le pose dans le cache plutôt que de le relire. */
  const poser = (vue: SourceCongesView): void => {
    cache.setQueryData(cle, vue);
    setErreurFichier(null);
  };

  const partage = useMutation({
    mutationFn: (shared: boolean) =>
      client.request<SourceCongesView>(`/case-files/${dossier.id}/holiday-source/shared`, {
        method: 'PUT',
        body: { shared },
      }),
    onSuccess: poser,
  });

  const choisir = useMutation({
    mutationFn: ({ kind, childId }: { kind: HolidaySourceKind; childId?: string }) =>
      client.request<SourceCongesView>(`/case-files/${dossier.id}/holiday-source`, {
        method: 'PUT',
        body: { kind, ...(childId ? { childId } : {}) },
      }),
    onSuccess: poser,
  });

  const deposer = useMutation({
    mutationFn: async ({ fichier, childId }: { fichier: File; childId?: string }) =>
      client.request<SourceCongesView>(`/case-files/${dossier.id}/holiday-source/ics`, {
        method: 'POST',
        body: {
          fileName: fichier.name,
          content: await fichier.text(),
          ...(childId ? { childId } : {}),
        },
      }),
    onSuccess: poser,
    onError: (erreur, variables) =>
      setErreurFichier({ childId: variables.childId ?? null, message: messageDErreur(erreur) }),
  });

  const retirer = useMutation({
    mutationFn: (childId?: string) =>
      client.request<SourceCongesView>(
        `/case-files/${dossier.id}/holiday-source/ics${childId ? `?childId=${childId}` : ''}`,
        { method: 'DELETE' },
      ),
    onSuccess: poser,
  });

  const vue = source.data;
  const titre = nomDuDossier(dossier.label, dossier.children, dossier.custodyKind);
  const enCours = partage.isPending || choisir.isPending || deposer.isPending || retirer.isPending;

  /** Les mêmes gestes, adressés au dossier ou à un enfant. */
  const commandes = (childId?: string) => ({
    enCours,
    identifiant: childId ?? 'dossier',
    surChoix: (kind: HolidaySourceKind) => choisir.mutate({ kind, ...(childId ? { childId } : {}) }),
    surDepot: (fichier: File) => deposer.mutate({ fichier, ...(childId ? { childId } : {}) }),
    surRetrait: () => retirer.mutate(childId),
    erreur:
      erreurFichier && erreurFichier.childId === (childId ?? null) ? erreurFichier.message : null,
  });

  return (
    <section className="compte-dossier source-conges">
      <h3>{titre}</h3>

      {vue ? (
        <>
          {/* L'option ne s'affiche qu'à partir de deux enfants : la poser sur un dossier qui
              n'en a qu'un serait une question sans objet. */}
          {vue.children.length > 1 ? (
            <>
              <div className="choix-theme" role="group" aria-label={m.congesMemes}>
                <button
                  type="button"
                  className={vue.shared ? 'choix-theme-option actif' : 'choix-theme-option'}
                  aria-pressed={vue.shared}
                  onClick={() => partage.mutate(true)}
                  disabled={enCours || vue.shared}
                >
                  {m.congesMemesOui}
                </button>
                <button
                  type="button"
                  className={!vue.shared ? 'choix-theme-option actif' : 'choix-theme-option'}
                  aria-pressed={!vue.shared}
                  onClick={() => partage.mutate(false)}
                  disabled={enCours || !vue.shared}
                >
                  {m.congesMemesNon}
                </button>
              </div>
              <p className="discret">{m.congesMemesAide}</p>
            </>
          ) : null}

          {vue.shared ? (
            <BlocSource source={vue.dossier} m={m} locale={locale} {...commandes()} />
          ) : (
            vue.children.map((enfant) => (
              <div key={enfant.childId} className="source-conges-enfant">
                <h4>
                  {/* La pastille porte la couleur qui encadrera ses jours au calendrier :
                      le réglage et la grille se répondent sans qu'on ait à les rapprocher
                      de tête. Le prénom est là pour qui ne voit pas la couleur. */}
                  <span
                    className="temoin-enfant"
                    style={{ '--teinte': teinteEnfant(enfant.color) } as CSSProperties}
                    aria-hidden="true"
                  />
                  {m.congesEnfant(enfant.firstName)}
                </h4>
                <BlocSource source={enfant} m={m} locale={locale} {...commandes(enfant.childId)} />
              </div>
            ))
          )}

          {choisir.isError ? (
            <Alerte titre={m.congesErreur}>{messageDErreur(choisir.error)}</Alerte>
          ) : null}

          <p className="discret" style={{ marginBottom: 0 }}>
            {m.congesPartage}
          </p>
        </>
      ) : null}
    </section>
  );
}

/**
 * Le réglage d'une source, qu'elle couvre le dossier ou un enfant.
 *
 * Le même bloc dans les deux cas : les gestes sont les mêmes — choisir entre deux sources,
 * déposer un fichier, le retirer — et les écrire deux fois les aurait fait diverger au
 * premier ajustement.
 */
function BlocSource({
  source,
  m,
  locale,
  enCours,
  erreur,
  surChoix,
  surDepot,
  surRetrait,
  identifiant,
}: {
  source: SourceEnVigueur;
  m: Messages['compte'];
  locale: Locale;
  enCours: boolean;
  erreur: string | null;
  surChoix: (kind: HolidaySourceKind) => void;
  surDepot: (fichier: File) => void;
  surRetrait: () => void;
  identifiant: string;
}) {
  const champ = `conges-ics-${identifiant}`;

  return (
    <>
      {/* Deux boutons plutôt qu'une bascule : ils nomment ce qu'ils font, là où une bascule
          laisse deviner quel état signifie quoi. `aria-pressed` dit lequel est en vigueur —
          c'est le même geste que l'apparence et la lecture des justificatifs, un peu plus
          haut sur cet écran. */}
      <div className="choix-theme" role="group" aria-label={m.conges}>
        <button
          type="button"
          className={
            source.kind === 'education_gouv' ? 'choix-theme-option actif' : 'choix-theme-option'
          }
          aria-pressed={source.kind === 'education_gouv'}
          onClick={() => surChoix('education_gouv')}
          disabled={enCours || source.kind === 'education_gouv'}
        >
          {m.congesOfficiel}
        </button>
        <button
          type="button"
          className={source.kind === 'ics' ? 'choix-theme-option actif' : 'choix-theme-option'}
          aria-pressed={source.kind === 'ics'}
          onClick={() => surChoix('ics')}
          // Choisir le fichier sans en avoir déposé un viderait le calendrier sans rien
          // expliquer. Le bouton attend qu'il y ait quelque chose à choisir.
          disabled={enCours || source.kind === 'ics' || !source.file}
        >
          {m.congesFichier}
        </button>
      </div>

      {/* Ce que la source en vigueur donne — et, quand elle ne donne rien, pourquoi. Un
          contour absent du calendrier ne dit pas de lui-même s'il manque un code postal ou
          si le produit est en panne. */}
      <p className="discret">
        {source.kind === 'education_gouv'
          ? source.zone
            ? m.congesOfficielZone(source.zone)
            : m.congesOfficielSansZone
          : m.congesFichierAide}
      </p>

      {erreur ? <Alerte titre={m.congesErreur}>{erreur}</Alerte> : null}

      {source.file ? (
        <p className="discret">
          {m.congesDepot(source.file.name, source.file.periodCount)}
          {source.file.coverage
            ? ` ${m.congesCouverture(jour(source.file.coverage.from, locale), jour(source.file.coverage.to, locale))}`
            : ''}
        </p>
      ) : null}

      {/* Le champ natif est masqué et son étiquette fait le bouton : un
          `<input type="file">` visible impose son propre libellé, hors charte et
          intraduisible. Il reste focusable, et l'étiquette lui donne son nom. */}
      <input
        id={champ}
        type="file"
        accept=".ics,text/calendar"
        disabled={enCours}
        onChange={(evenement) => {
          const fichier = evenement.target.files?.[0];
          if (fichier) surDepot(fichier);
          // Vidé pour que redéposer le **même** fichier redéclenche l'événement : sans
          // cela, corriger un dépôt raté demanderait un autre fichier.
          evenement.target.value = '';
        }}
      />
      <label htmlFor={champ} className="bouton secondaire">
        {enCours ? m.congesDepotEnCours : m.congesDeposer}
      </label>

      {source.file ? (
        <Bouton variante="lien" onClick={surRetrait} disabled={enCours}>
          {m.congesRetirer}
        </Bouton>
      ) : null}
    </>
  );
}
